import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Sparkles } from "lucide-react";
import { TOOL_ICONS, ACCENT_CLASSES } from "../../lib/toolDefaults";
import type { TickerSlot } from "../../hooks/useInsightTickerSlots";
import type { ToolAccent } from "../../lib/types";

interface Props {
  slots: TickerSlot[];
  questionText: string;
  onClose: () => void;
}

const ACCENT_BORDER: Record<ToolAccent, string> = {
  berry: "border-rc-berry",
  lime: "border-rc-lime",
  paper: "border-rc-paper/60",
  mist: "border-rc-mist",
};

/**
 * Full-screen detail view of the ticker's insight slots: one tab per tool,
 * hero typography for the room to read and discuss. Opened by clicking the
 * insight ticker; read-only, so it needs no auth. Content follows the same
 * realtime slot model as the ticker — re-running a tool updates the open tab.
 */
export default function StageInsightOverlay({ slots, questionText, onClose }: Props) {
  const [activeToolId, setActiveToolId] = useState<string | null>(slots[0]?.tool.id ?? null);

  // Fall back to the first slot when the active tool's slot disappears
  // (tool disabled, insight hidden, or survey rotation swapped the question).
  const activeSlot = slots.find((s) => s.tool.id === activeToolId) ?? slots[0] ?? null;
  const effectiveToolId = activeSlot?.tool.id ?? null;

  // Capture-phase keys: Escape closes, arrows switch tabs. preventDefault +
  // stopPropagation keep the stage carousel's window-level arrow navigation
  // (bubble phase) from firing while the overlay is up.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        if (slots.length < 2 || !effectiveToolId) return;
        const idx = slots.findIndex((s) => s.tool.id === effectiveToolId);
        const delta = e.key === "ArrowRight" ? 1 : -1;
        const next = slots[(idx + delta + slots.length) % slots.length];
        setActiveToolId(next.tool.id);
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [slots, effectiveToolId, onClose]);

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[90] bg-rc-ink/95 backdrop-blur-sm text-rc-paper font-sans antialiased flex flex-col animate-fade-up"
      role="dialog"
      aria-modal="true"
      aria-label="KI-Insights im Detail"
    >
      {/* Header */}
      <header className="flex items-start justify-between gap-8 px-12 pt-10 pb-6 flex-shrink-0">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold tracking-[0.28em] text-rc-berry uppercase mb-3">
            KI-Insights
          </p>
          <h2
            className="font-bold tracking-[-0.02em] leading-[1.15] text-balance"
            style={{ fontSize: "clamp(1.2rem, 2vw, 1.8rem)", maxWidth: "40ch" }}
          >
            {questionText}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="flex-shrink-0 w-10 h-10 rounded-full bg-rc-paper/8 hover:bg-rc-paper/15 text-rc-paper/70 hover:text-rc-paper flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </header>

      {slots.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-rc-stone text-[18px]">Noch keine Insights für diese Frage.</p>
        </div>
      ) : (
        <>
          {/* Tab bar */}
          <div className="flex items-center gap-2.5 px-12 pb-8 flex-shrink-0 flex-wrap">
            {slots.map((slot) => {
              const Icon = TOOL_ICONS[slot.tool.icon] ?? Sparkles;
              const accent = ACCENT_CLASSES[slot.tool.accent];
              const active = slot.tool.id === effectiveToolId;
              return (
                <button
                  key={slot.tool.id}
                  type="button"
                  onClick={() => setActiveToolId(slot.tool.id)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-2.5 rounded-pill px-4 py-2 text-[13px] font-semibold border transition-colors ${
                    active
                      ? `${accent.stageBorder} ${accent.stageBg} ${accent.stageLabelText}`
                      : "border-rc-paper/10 text-rc-paper/60 hover:text-rc-paper hover:border-rc-paper/25"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-md flex items-center justify-center ${
                      active ? `${accent.stageIconBg} ${accent.stageIconText}` : "bg-rc-paper/10 text-rc-paper/60"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  {slot.tool.label}
                </button>
              );
            })}
          </div>

          {/* Content pane */}
          {activeSlot && (
            <div key={activeSlot.tool.id} className="flex-1 min-h-0 overflow-y-auto px-12 pb-8 animate-fade-up">
              <blockquote
                className={`border-l-[3px] pl-8 py-2 ${ACCENT_BORDER[activeSlot.tool.accent]}`}
              >
                <p
                  className="text-rc-paper leading-[1.4] text-balance"
                  style={{ fontSize: "clamp(1.6rem, 2.4vw, 2.4rem)", maxWidth: "40ch" }}
                >
                  {activeSlot.insight.content}
                </p>
              </blockquote>
              <p className="text-rc-stone text-[13px] mt-6 pl-8">
                {activeSlot.insight.responses_at_run != null
                  ? `Basiert auf ${activeSlot.insight.responses_at_run} Antworten · `
                  : ""}
                {formatTime(activeSlot.insight.created_at)} Uhr
              </p>
            </div>
          )}

          {/* Footer hint */}
          <footer className="px-12 pb-6 flex-shrink-0">
            <p className="text-rc-stone/60 text-[11px] font-semibold tracking-[0.18em] uppercase">
              ←/→ Tools wechseln · Esc schließen
            </p>
          </footer>
        </>
      )}
    </div>,
    document.body,
  );
}
